import { Formik, FormikHelpers, FormikProps } from "formik";
import React, { FC, memo, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { LoadingSpinner } from "@/components/Loading";
import { Text } from "@/components/Text";
import { IConsultantProfileDto, IConsultantProfileLanguage } from "@/data/consultant-profiles";
import { fetchSuggestedProfileData } from "@/data/profile-data";
import { IConsultantProfileRole } from "@/data/roles";
import { Flex } from "@/eworkitComponents";
import { FormikForm } from "@/formikComponents";
import { useFetcher } from "@/hooks/http";
import { Messenger } from "@/views/components/Messenger";

import {
  IProfileLanguagesSectionValues,
  ProfileLanguagesSection,
  profileLanguagesSectionValidationSchema,
} from "./ProfileLanguagesSection";
import {
  IProfileLocationSectionValues,
  ProfileLocationSection,
  profileLocationSectionValidationSchema,
} from "./ProfileLocationSection";
import {
  IProfileRoleSectionValues,
  ProfileRoleSection,
  profileRoleSectionValidationSchema,
} from "./ProfileRoleSection";
import {
  IProfileSkillsSectionValues,
  ProfileSkillsSection,
  profileSkillsSectionValidationSchema,
} from "./ProfileSkillsSection";

export interface IProfileSuggestedDataFormValues
  extends IProfileRoleSectionValues,
    IProfileSkillsSectionValues,
    IProfileLanguagesSectionValues,
    IProfileLocationSectionValues {}

const FORM_INITIAL_VALUES: IProfileSuggestedDataFormValues = {
  roles: [{ role: null as any } as IConsultantProfileRole],
  skills: [],
  languages: [{} as IConsultantProfileLanguage],
  location: null,
};

const formValidationSchema = (showLocation?: boolean, skillsRequired?: boolean, languagesRequired?: boolean) => {
  let result = profileRoleSectionValidationSchema()
    .concat(profileSkillsSectionValidationSchema(skillsRequired))
    .concat(profileLanguagesSectionValidationSchema(languagesRequired));

  if (showLocation) {
    result = result.concat(profileLocationSectionValidationSchema);
  }

  return result;
};

export const PROFILE_SUGGESTED_DATA_MESSENGER_CONTEXT = "profile-suggested-data-form";

interface IProfileSuggestedDataFormProps {
  form: FormikProps<IProfileSuggestedDataFormValues>;
  onCancel: () => void;
  profile: IConsultantProfileDto;
  onValuesChange?: (values: IProfileSuggestedDataFormValues) => void;
  showLocation?: boolean;
  skillsRequired?: boolean;
  buttonCancelText?: string;
  buttonSubmitText?: string;
  languagesRequired?: boolean;
}

const ProfileSuggestedDataForm: FC<IProfileSuggestedDataFormProps> = ({
  showLocation,
  skillsRequired,
  languagesRequired,
  form,
  onValuesChange,
  profile,
  onCancel,
  buttonCancelText,
  buttonSubmitText,
}) => {
  const { t } = useTranslation("session");

  const values = form.values;

  useEffect(() => {
    if (onValuesChange) {
      onValuesChange(values);
    }
  }, [values]);

  const suggestedDataFetcher = useFetcher(fetchSuggestedProfileData, { cvIds: profile.cvs?.map(cv => cv.id) }, []);

  const suggestedData = suggestedDataFetcher.payload;

  return (
    <FormikForm>
      <Flex direction="column" innerMargin="xl">
        <Messenger context={PROFILE_SUGGESTED_DATA_MESSENGER_CONTEXT} />

        <ProfileRoleSection form={form} suggestedRoles={suggestedData?.roles} />
        <ProfileSkillsSection form={form} profile={profile} required={skillsRequired} />
        <ProfileLanguagesSection
          form={form}
          suggestedLanguages={suggestedData?.languages}
          required={languagesRequired}
        />
        {showLocation && <ProfileLocationSection form={form} suggestedLocations={suggestedData?.locations} />}

        <Flex grow={1} justify="flex-end" innerMargin="md" padding={["xl", false, false, false]}>
          <Button variant="secondary" onClick={onCancel} size="lg" testName="cancel" disabled={form.isSubmitting}>
            {buttonCancelText || t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.BUTTON_BACK")}
          </Button>
          <Button type="submit" disabled={form.isSubmitting} size="lg" testName="submit">
            {form.isSubmitting ? (
              <Flex direction="row" innerMargin="sm" align="center" justify="center">
                <LoadingSpinner />
                <Text>{t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.BUTTON_SAVING")}</Text>
              </Flex>
            ) : (
              <Text>
                {buttonSubmitText || t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.BUTTON_FINISH")}
              </Text>
            )}
          </Button>
        </Flex>
      </Flex>
    </FormikForm>
  );
};

interface IProfileSuggestedDataFormikProps extends IProfileSuggestedDataFormProps {
  initialValues?: IProfileSuggestedDataFormValues;
  onSubmit: (
    values: IProfileSuggestedDataFormValues,
    actions: FormikHelpers<IProfileSuggestedDataFormValues>,
  ) => Promise<void>;
  onCancel: () => void;
}

export const ProfileSuggestedDataFormik: FC<Omit<IProfileSuggestedDataFormikProps, "form">> = memo(
  ({ initialValues = FORM_INITIAL_VALUES, onSubmit, ...rest }) => (
    <Formik
      initialValues={initialValues}
      validationSchema={formValidationSchema(rest.showLocation, rest.skillsRequired)}
      onSubmit={onSubmit}>
      {form => <ProfileSuggestedDataForm form={form} {...rest} />}
    </Formik>
  ),
);
